import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { useMutation } from '@apollo/client';
import gql from 'graphql-tag';
import Modal from '../../../../../ui/canon/Modal.jsx';
import MarkdownMessage from '../../../assistant/components/MarkdownMessage.jsx';
import CollapsibleSources from '../../../assistant/components/CollapsibleSources.jsx';
import { formatAIChatTime } from '../Messaging/shared/dateUtils';
import { SparklesIcon, XMarkIcon } from '@heroicons/react/24/solid';
import { Loader2 as Loader2Icon, Send as SendIcon, RefreshCw as RefreshIcon, BookOpen as BookOpenIcon } from 'lucide-react';
import CopyMessageButton from './CopyMessageButton';

// Wrapper component for assistant message content that provides a ref for copying
const AssistantMessageContent = ({ msg, onSendMessage }) => {
  const contentRef = React.useRef(null);

  return (
    <>
      <div ref={contentRef}>
        <div className="chat_result" dangerouslySetInnerHTML={{ __html: msg.content }} />
        {/* Widget rendering */}
        {msg.widget && (
          <div className="mt-4 p-4 bg-succession-bg-secondary rounded-lg border border-succession-border">
            <ValuationWidget widget={msg.widget} />
          </div>
        )}
      </div>
      <CopyMessageButton
        containerRef={contentRef}
        isHtml={true}
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100"
      />
    </>
  );
};

// Simple widget renderer for valuation data
const ValuationWidget = ({ widget }) => {
  const intl = useIntl();
  if (!widget || !widget.type) return null;

  const { type, data } = widget;

  switch (type) {
    case 'valuation_summary':
      return (
        <div className="space-y-3">
          <h4 className="font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_valuation_summary' })}</h4>
          {data?.lines && (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-succession-border">
                    <th className="text-left py-2 text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_table_line' })}</th>
                    <th className="text-right py-2 text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_table_value' })}</th>
                    <th className="text-right py-2 text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_table_pct' })}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.lines.map((line, idx) => (
                    <tr key={idx} className="border-b border-succession-border/50">
                      <td className="py-2 text-succession-text-primary">{line.name}</td>
                      <td className="py-2 text-right text-succession-text-primary">{line.value}</td>
                      <td className="py-2 text-right text-succession-text-secondary">{line.percentage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {data?.total && (
            <div className="flex justify-between pt-2 font-semibold">
              <span>{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_total' })}</span>
              <span>{data.total}</span>
            </div>
          )}
        </div>
      );

    case 'scenario_comparison':
      return (
        <div className="space-y-3">
          <h4 className="font-semibold text-succession-text-primary">
            {data?.title || intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_scenario_comparison' })}
          </h4>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="bg-succession-bg-tertiary p-3 rounded-lg text-center">
              <div className="text-succession-text-secondary text-xs uppercase tracking-wide">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_before' })}</div>
              <div className="text-xl font-bold text-succession-text-primary mt-1">{data?.before}</div>
            </div>
            <div className="bg-succession-bg-tertiary p-3 rounded-lg text-center">
              <div className="text-succession-text-secondary text-xs uppercase tracking-wide">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_after' })}</div>
              <div className="text-xl font-bold text-succession-success mt-1">{data?.after}</div>
            </div>
            <div className="bg-succession-bg-tertiary p-3 rounded-lg text-center">
              <div className="text-succession-text-secondary text-xs uppercase tracking-wide">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_impact' })}</div>
              <div className={`text-xl font-bold mt-1 ${data?.changeIsPositive ? 'text-succession-success' : 'text-succession-error'}`}>
                {data?.change}
              </div>
            </div>
          </div>
          {data?.line_impact && (
            <div className="text-sm text-succession-text-secondary border-t border-succession-border pt-2 mt-2">
              <span className="font-medium">{data.line_impact.name}:</span> {data.line_impact.before} → {data.line_impact.after} ({data.line_impact.change})
            </div>
          )}
          {data?.changes_applied && data.changes_applied.length > 0 && (
            <div className="text-xs text-succession-text-secondary">
              {intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_changes' })} {data.changes_applied.join(' • ')}
            </div>
          )}
        </div>
      );

    case 'method_comparison':
      return (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-succession-text-primary">
              {data?.line ? `${data.line} ${intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_line_suffix' })}` : intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_method_comparison' })}
            </h4>
            {data?.blended_value && (
              <div className="text-right">
                <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_blended_value' })}</div>
                <div className="font-bold text-succession-secondary">{data.blended_value}</div>
              </div>
            )}
          </div>
          <div className="space-y-2">
            {data?.methods?.map((method, idx) => (
              <div key={idx} className="flex justify-between items-center py-3 px-3 bg-succession-bg-tertiary rounded-lg">
                <div className="flex-1">
                  <div className="font-medium text-succession-text-primary">{method.name}</div>
                  <div className="text-xs text-succession-text-secondary">{method.description}</div>
                  {method.range && (
                    <div className="text-xs text-succession-text-secondary mt-1">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_range' })} {method.range}</div>
                  )}
                </div>
                <div className="text-right ml-4">
                  <div className="font-bold text-lg text-succession-text-primary">{method.value}</div>
                  <div className="text-xs text-succession-secondary font-medium">{method.weight}</div>
                </div>
              </div>
            ))}
          </div>
          {data?.insight && (
            <div className="text-sm text-succession-text-secondary bg-succession-secondary/5 p-3 rounded-lg border-l-2 border-succession-secondary">
              {data.insight}
            </div>
          )}
        </div>
      );

    case 'recommendations':
      return (
        <div className="space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="font-semibold text-succession-text-primary">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_recommendations' })}</h4>
            {data?.current_valuation && (
              <div className="text-right">
                <div className="text-xs text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_current_value' })}</div>
                <div className="font-bold text-succession-text-primary">{data.current_valuation}</div>
              </div>
            )}
          </div>
          <div className="space-y-2">
            {data?.items?.map((item, idx) => (
              <div key={idx} className="flex gap-3 p-3 bg-succession-bg-tertiary rounded-lg hover:bg-succession-bg-primary transition-colors">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                  item.priority === 1 ? 'bg-succession-error/20 text-succession-error' :
                  item.priority === 2 ? 'bg-succession-warning/20 text-succession-warning' :
                  'bg-succession-success/20 text-succession-success'
                }`}>
                  {item.priority}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-medium text-succession-text-primary">{item.title}</div>
                    {item.line && item.line !== 'General' && (
                      <span className="text-xs px-2 py-0.5 bg-succession-secondary/10 text-succession-secondary rounded-full">
                        {item.line}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-succession-text-secondary mt-1">{item.description}</div>
                  <div className="flex items-center gap-4 mt-2">
                    {item.impact && (
                      <div className={`text-xs font-medium ${
                        item.impact === 'High' ? 'text-succession-success' :
                        item.impact === 'Medium' ? 'text-succession-warning' :
                        'text-succession-text-secondary'
                      }`}>
                        {intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_impact_label' })} {item.impact}
                      </div>
                    )}
                    {item.estimated_value_add && (
                      <div className="text-xs text-succession-success font-medium">
                        +{item.estimated_value_add}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {data?.total_potential && data.total_potential !== '$0' && (
            <div className="flex justify-between items-center pt-2 border-t border-succession-border text-sm">
              <span className="text-succession-text-secondary">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.widget_total_potential' })}</span>
              <span className="font-bold text-succession-success">{data.total_potential}</span>
            </div>
          )}
        </div>
      );

    default:
      // Fallback: render as JSON
      return (
        <div className="text-sm">
          <pre className="overflow-x-auto p-2 bg-succession-bg-tertiary rounded text-succession-text-secondary">
            {JSON.stringify(data, null, 2)}
          </pre>
        </div>
      );
  }
};

// GraphQL mutation for agent interactions
const INTERACT_WITH_MULTI_LINE_AGENT = gql`
  mutation InteractWithMultiLineAgent($agentType: String!, $message: String!, $conversationId: String, $context: JSON!) {
    interactWithAgent(agentType: $agentType, message: $message, conversationId: $conversationId, context: $context) {
      response
      conversationId
      widgetType
      widgetProps
      nextActions
      updatedMultiLineState
      ragSources
      chatHistory {
        id
        role
        content
        timestamp
        suggestedActions
        processingTime
      }
      conversationInfo {
        sessionId
        startedAt
        messageCount
        lastActivity
        topicsDiscussed
        durationMinutes
      }
    }
  }
`;

/**
 * MultiLineValuationChatWindow - Chat agent for multi-line valuation page
 *
 * Provides conversational interface for:
 * - Understanding multi-line valuations
 * - Running what-if scenarios
 * - Comparing valuation methods
 * - Getting recommendations
 */
const MultiLineValuationChatWindow = ({
  user,
  clientState,
  onStateUpdate,
  onClose,
  conversationId: persistedConversationId = null,
  onConversationIdChange = null
}) => {
  const intl = useIntl();
  const [interactWithAgent] = useMutation(INTERACT_WITH_MULTI_LINE_AGENT);
  const [messages, setMessages] = useState([]);
  const [agentId, setAgentId] = useState(persistedConversationId);
  const [conversationInfo, setConversationInfo] = useState(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isThinking, setIsThinking] = useState(false);
  const [input, setInput] = useState('');
  const scrollRef = useRef(null);
  const pinnedRef = useRef(true);

  // Keep a ref to always get latest clientState (avoids stale closure in useEffect)
  const clientStateRef = useRef(clientState);
  useEffect(() => {
    clientStateRef.current = clientState;
  }, [clientState]);

  // Sync agentId back to parent when it changes
  useEffect(() => {
    if (agentId && onConversationIdChange) {
      onConversationIdChange(agentId);
    }
  }, [agentId, onConversationIdChange]);

  // Initialize agent and load chat history
  useEffect(() => {
    const initializeAgent = async () => {
      setIsLoadingHistory(true);

      // Use ref to get latest clientState (avoids stale closure)
      const currentClientState = clientStateRef.current;

      try {
        const { data: response } = await interactWithAgent({
          variables: {
            agentType: 'multi_line_valuation_chat_agent',
            message: 'INIT_WELCOME_MESSAGE',
            conversationId: persistedConversationId,
            context: {
              user_id: user?.id,
              page_context: 'multi_line_valuation',
              client_state: currentClientState
            }
          }
        });

        if (response?.interactWithAgent) {
          const { chatHistory, conversationInfo: convInfo, response: welcomeResponse } = response.interactWithAgent;

          if (convInfo) {
            setConversationInfo(convInfo);
            setAgentId(convInfo.sessionId);
          }

          // Always use fresh welcome for multi-line page to reflect current state
          if (welcomeResponse) {
            const welcomeMsg = createWelcomeMessage(welcomeResponse);
            setMessages([welcomeMsg]);
          } else if (chatHistory && chatHistory.length > 0) {
            const convertedMessages = chatHistory
              .filter(msg => msg.role !== 'system')
              .filter(msg => msg.content !== 'INIT_WELCOME_MESSAGE')
              .map(msg => convertToMessageFormat(msg));

            if (convertedMessages.length > 0) {
              setMessages(convertedMessages);
            } else {
              setMessages([createFallbackWelcome()]);
            }
          } else {
            setMessages([createFallbackWelcome()]);
          }
        } else {
          setMessages([createFallbackWelcome()]);
        }
      } catch (error) {
        console.error('Failed to initialize multi-line valuation agent:', error);
        setMessages([createFallbackWelcome()]);
      } finally {
        setIsLoadingHistory(false);
      }
    };

    initializeAgent();
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
    if (atBottom || pinnedRef.current) {
      el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
    }
  }, [messages, isThinking]);

  const createFallbackWelcome = () => ({
    id: `msg-${Date.now()}`,
    role: 'assistant',
    content: intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.fallback_welcome' }),
    timestamp: formatAIChatTime(new Date()),
    suggestedActions: []
  });

  const createWelcomeMessage = (response) => {
    const parsed = parseAgentResponse(response);
    return {
      id: `msg-${Date.now()}`,
      role: 'assistant',
      content: parsed.message,
      timestamp: formatAIChatTime(new Date()),
      suggestedActions: parsed.suggestedActions || []
    };
  };

  const convertToMessageFormat = (msg) => {
    if (msg.role === 'assistant' || msg.role === 'agent') {
      const parsed = parseAgentResponse(msg.content);
      return {
        id: msg.id || `msg-${Date.now()}`,
        role: 'assistant',
        content: parsed.message,
        timestamp: formatAIChatTime(new Date(msg.timestamp)),
        suggestedActions: parsed.suggestedActions || msg.suggestedActions || []
      };
    }

    return {
      id: msg.id || `msg-${Date.now()}`,
      role: 'user',
      content: msg.content,
      timestamp: formatAIChatTime(new Date(msg.timestamp)),
      suggestedActions: []
    };
  };

  const parseAgentResponse = (response) => {
    let message = response || '';
    let suggestedActions = [];
    let widget = null;

    // Strip out any raw JSON tool calls that may have leaked through
    // These look like: {"tool": "...", "args": {...}}
    message = message.replace(/^\s*\{\s*"tool"\s*:\s*"[^"]+"\s*,\s*"args"\s*:\s*\{[\s\S]*?\}\s*\}\s*/gm, '').trim();
    message = message.replace(/\{\s*"tool"\s*:\s*"[^"]+"\s*,\s*"args"\s*:\s*\{[^}]*\}\s*\}/g, '').trim();

    // Try to extract content from markdown JSON code blocks and create widgets
    const jsonBlockMatch = message.match(/```json\s*([\s\S]*?)```/);
    if (jsonBlockMatch && jsonBlockMatch[1].trim()) {
      try {
        const jsonData = JSON.parse(jsonBlockMatch[1].trim());
        // Try to create a widget from the JSON data
        if (jsonData.type || jsonData.lines || jsonData.methods || jsonData.items) {
          widget = {
            type: jsonData.type || 'valuation_summary',
            data: jsonData
          };
        }
        // Remove the code block from message
        message = message.replace(/```json\s*[\s\S]*?```/g, '').trim();
      } catch (e) {
        // If JSON parsing fails, just strip the empty/malformed blocks
        message = message.replace(/```json\s*```/g, '').trim();
      }
    } else {
      // Strip empty code blocks
      message = message.replace(/```json\s*```/g, '').trim();
    }
    message = message.replace(/```\s*\{[\s\S]*?\}\s*```/g, '').trim();

    // Check for widget markers [WIDGET:type]...[/WIDGET]
    const widgetMatch = message.match(/\[WIDGET:([\w_]+)\](.*?)\[\/WIDGET\]/s);
    if (widgetMatch) {
      const widgetType = widgetMatch[1];
      try {
        const widgetData = JSON.parse(widgetMatch[2]);
        widget = { type: widgetType, data: widgetData };
      } catch (e) {
        console.warn('Failed to parse widget data:', e);
      }
      message = message.replace(/\[WIDGET:[\w_]+\].*?\[\/WIDGET\]/s, '').trim();
    }

    // Extract MESSAGE: section if present
    if (message.includes('MESSAGE:')) {
      const messageMatch = message.match(/MESSAGE:\s*(.*?)(?=SUGGESTED_ACTIONS:|$)/s);
      if (messageMatch) {
        message = messageMatch[1].trim();
      }
    }

    // Extract SUGGESTED_ACTIONS: section
    const suggestedMatch = response.match(/SUGGESTED_ACTIONS:\s*(.+?)$/s);
    if (suggestedMatch) {
      const actionsText = suggestedMatch[1].trim();
      suggestedActions = actionsText
        .split(/[,\n]/)
        .map(s => s.trim().replace(/^[-\d.)\s]+/, ''))
        .filter(s => s.length > 0 && s.length < 100);
    }

    // Also check for "Suggested actions:" format (from format_graphql_response)
    const altActionsMatch = message.match(/\n\nSuggested actions:\s*(.+)$/);
    if (altActionsMatch) {
      const actions = altActionsMatch[1].split(', ').map(s => s.trim()).filter(s => s);
      if (actions.length > 0) {
        suggestedActions = actions;
      }
      message = message.replace(/\n\nSuggested actions:.+$/, '').trim();
    }

    // Clean up any remaining format markers
    message = message.replace(/^MESSAGE:\s*/i, '').trim();

    // Default actions if none parsed
    if (suggestedActions.length === 0) {
      suggestedActions = [
        intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.default_action_breakdown' }),
        intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.default_action_scenario' }),
        intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.default_action_compare' })
      ];
    }

    // Fallback message if empty but we have a widget
    if (!message && widget) {
      message = intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.fallback_found' });
    } else if (!message && !widget) {
      message = intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.fallback_error' });
    }

    return { message, suggestedActions, widget };
  };

  const handleSendMessage = async (messageText) => {
    if (!messageText?.trim() || isThinking) return;

    // Add user message immediately
    const userMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: messageText.trim(),
      timestamp: formatAIChatTime(new Date())
    };

    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setIsThinking(true);
    pinnedRef.current = true;

    try {
      const { data: interaction } = await interactWithAgent({
        variables: {
          agentType: 'multi_line_valuation_chat_agent',
          message: messageText,
          conversationId: agentId,
          context: {
            user_id: user?.id,
            page_context: 'multi_line_valuation',
            client_state: clientStateRef.current
          }
        }
      });

      if (interaction?.interactWithAgent?.response) {
        const parsed = parseAgentResponse(interaction.interactWithAgent.response);

        // Check if multi-line state was updated
        if (interaction.interactWithAgent.updatedMultiLineState && onStateUpdate) {
          console.log('Multi-line state updated by agent:', interaction.interactWithAgent.updatedMultiLineState);
          onStateUpdate(interaction.interactWithAgent.updatedMultiLineState);
        }

        // Also check for widget from GraphQL response (server-generated widgets take precedence)
        let widget = parsed.widget;
        if (!widget && interaction.interactWithAgent.widgetType && interaction.interactWithAgent.widgetProps) {
          widget = {
            type: interaction.interactWithAgent.widgetType,
            data: interaction.interactWithAgent.widgetProps
          };
        }

        // Use server-provided suggested actions if available, otherwise use parsed ones
        const suggestedActions = (interaction.interactWithAgent.nextActions?.length > 0)
          ? interaction.interactWithAgent.nextActions
          : parsed.suggestedActions;

        const agentMessage = {
          id: `msg-${Date.now() + 1}`,
          role: 'assistant',
          content: parsed.message,
          timestamp: formatAIChatTime(new Date()),
          suggestedActions: suggestedActions,
          widget: widget,
          ragSources: interaction.interactWithAgent.ragSources || []
        };

        setMessages([...newMessages, agentMessage]);

        // Update conversation info if provided
        if (interaction.interactWithAgent.conversationInfo) {
          setConversationInfo(interaction.interactWithAgent.conversationInfo);
          if (interaction.interactWithAgent.conversationInfo.sessionId) {
            setAgentId(interaction.interactWithAgent.conversationInfo.sessionId);
          }
        }
      } else {
        throw new Error('No response from agent');
      }
    } catch (error) {
      console.error('Error processing message:', error);

      const errorMessage = {
        id: `msg-${Date.now() + 1}`,
        role: 'assistant',
        content: intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.error_rephrasing' }),
        timestamp: formatAIChatTime(new Date())
      };

      setMessages([...newMessages, errorMessage]);
    } finally {
      setIsThinking(false);
    }
  };

  const startNewConversation = async () => {
    try {
      setMessages([]);
      setAgentId(null);
      setConversationInfo(null);
      setIsLoadingHistory(true);

      const result = await interactWithAgent({
        variables: {
          agentType: 'multi_line_valuation_chat_agent',
          message: 'START_NEW_CONVERSATION',
          context: {
            page_context: 'multi_line_valuation',
            client_state: clientStateRef.current,
            reset: true
          }
        }
      });

      if (result.data?.interactWithAgent) {
        const response = result.data.interactWithAgent;
        if (response.conversationInfo) {
          setAgentId(response.conversationInfo.sessionId);
          setConversationInfo(response.conversationInfo);
        }

        if (response.response) {
          const welcomeMsg = createWelcomeMessage(response.response);
          setMessages([welcomeMsg]);
        }
      }
    } catch (error) {
      console.error('Failed to start new conversation:', error);
      setMessages([createFallbackWelcome()]);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage(input);
      setInput('');
    }
    if (e.key === 'Escape') {
      onClose?.();
    }
  };

  const suggestedTopics = [
    {
      id: 'breakdown',
      title: "Show my valuation breakdown",
      description: 'See how each business line contributes'
    },
    {
      id: 'scenario',
      title: 'Run a what-if scenario',
      description: 'Explore how changes affect your value'
    },
    {
      id: 'methods',
      title: 'Compare valuation methods',
      description: 'Understand DCF, MOR, EBITDA, Asset'
    },
    {
      id: 'recommendations',
      title: 'How can I increase my value?',
      description: 'Get personalized recommendations'
    }
  ];

  const isEmpty = messages.length === 0 && !isThinking && !isLoadingHistory;

  return (
    <Modal
      open={true}
      onClose={onClose}
      overlayClassName="fixed inset-0 z-50" overlayStyle={{ backgroundColor: 'rgba(0, 0, 0, 0.7)' }}
      panelClassName="absolute inset-0 flex items-center justify-center p-2"
      className="bg-succession-bg-secondary text-succession-text-primary rounded-xl shadow-succession-lg border border-succession-border w-[92vw] h-[95vh] flex flex-col"
    >
      <div className="flex flex-col h-full w-full" style={{ maxHeight: '95vh', maxWidth: '92vw' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 flex-shrink-0 bg-succession-bg-primary border-b border-succession-border">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-succession-secondary/10 flex items-center justify-center">
              <SparklesIcon className="h-5 w-5 text-succession-secondary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold m-0 p-0">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.title' })}</h2>
              <p className="text-sm text-succession-text-secondary m-0 p-0">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.subtitle' })}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={startNewConversation}
              className="p-2 hover:bg-succession-bg-secondary rounded-lg transition-colors"
              title="Start new conversation"
            >
              <RefreshIcon className="h-5 w-5 text-succession-text-secondary" />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-succession-bg-secondary rounded-lg transition-colors"
            >
              <XMarkIcon className="h-5 w-5 text-succession-text-secondary" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto px-6 py-4"
          style={{ minHeight: 0 }}
        >
          {isLoadingHistory ? (
            <div className="flex items-center justify-center h-full">
              <div className="flex items-center gap-3 text-succession-text-secondary">
                <Loader2Icon className="h-5 w-5 animate-spin" />
                <span>{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.loading_conversation' })}</span>
              </div>
            </div>
          ) : isEmpty ? (
            <div className="flex flex-col items-center justify-center h-full gap-6">
              <div className="text-center">
                <SparklesIcon className="h-12 w-12 text-succession-secondary mx-auto mb-4" />
                <h3 className="text-xl font-semibold mb-2">{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.title' })}</h3>
                <p className="text-succession-text-secondary max-w-md">
                  {intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.empty_description' })}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-3 max-w-2xl">
                {suggestedTopics.map(topic => (
                  <button
                    key={topic.id}
                    onClick={() => handleSendMessage(topic.title)}
                    className="p-4 text-left bg-succession-bg-primary hover:bg-succession-bg-tertiary border border-succession-border rounded-lg transition-colors"
                  >
                    <div className="font-medium text-succession-text-primary">{topic.title}</div>
                    <div className="text-sm text-succession-text-secondary mt-1">{topic.description}</div>
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="max-w-3xl mx-auto space-y-6">
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`flex gap-4 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                >
                  {msg.role === 'assistant' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-secondary/10 flex items-center justify-center">
                      <SparklesIcon className="h-4 w-4 text-succession-secondary" />
                    </div>
                  )}
                  <div
                    className={`max-w-[85%] rounded-lg px-4 py-3 ${
                      msg.role === 'user'
                        ? 'bg-succession-secondary text-white'
                        : 'bg-succession-bg-primary border border-succession-border relative group'
                    }`}
                  >
                    {msg.role === 'assistant' ? (
                      <AssistantMessageContent msg={msg} onSendMessage={handleSendMessage} />
                    ) : (
                      <div className="whitespace-pre-wrap">{msg.content}</div>
                    )}
                    <div className={`text-xs mt-2 ${msg.role === 'user' ? 'text-white/70' : 'text-succession-text-secondary'}`}>
                      {msg.timestamp}
                    </div>
                    {/* RAG Sources display - collapsible if too many */}
                    {msg.role === 'assistant' && (
                      <CollapsibleSources sources={msg.ragSources} collapsedLimit={2} />
                    )}
                    {msg.role === 'assistant' && msg.suggestedActions && msg.suggestedActions.length > 0 && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-succession-border/50">
                        {msg.suggestedActions.slice(0, 4).map((action, idx) => (
                          <button
                            key={idx}
                            onClick={() => handleSendMessage(action)}
                            className="text-xs px-3 py-1.5 bg-succession-secondary/10 hover:bg-succession-secondary/20 text-succession-secondary rounded-full transition-colors"
                          >
                            {action}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {msg.role === 'user' && (
                    <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-secondary flex items-center justify-center">
                      <span className="text-white text-sm font-medium">
                        {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </span>
                    </div>
                  )}
                </div>
              ))}
              {isThinking && (
                <div className="flex gap-4 justify-start">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-succession-secondary/10 flex items-center justify-center">
                    <SparklesIcon className="h-4 w-4 text-succession-secondary" />
                  </div>
                  <div className="bg-succession-bg-primary border border-succession-border rounded-lg px-4 py-3">
                    <div className="flex items-center gap-2 text-succession-text-secondary">
                      <Loader2Icon className="h-4 w-4 animate-spin" />
                      <span>{intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.analyzing' })}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Input Area */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-succession-border bg-succession-bg-primary">
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={intl.formatMessage({ id: 'succession.agent_system.multi_line_valuation_chat_window.ask_placeholder' })}
              className="flex-1 px-4 py-3 bg-succession-bg-secondary border border-succession-border rounded-lg focus:outline-none focus:ring-2 focus:ring-succession-secondary text-succession-text-primary placeholder-succession-text-secondary"
              disabled={isThinking || isLoadingHistory}
            />
            <button
              onClick={() => {
                handleSendMessage(input);
                setInput('');
              }}
              disabled={!input.trim() || isThinking || isLoadingHistory}
              className="p-3 bg-succession-secondary hover:bg-succession-secondary-hover disabled:opacity-50 disabled:cursor-not-allowed rounded-lg transition-colors"
            >
              <SendIcon className="h-5 w-5 text-white" />
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default MultiLineValuationChatWindow;
